// Display formatters shared across cards, viewer counts, and info bars.

/** 1204 -> "1.2K", 5800 -> "5.8K", 2_400_000 -> "2.4M". */
export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "0";
  if (n < 1000) return String(Math.floor(n));
  if (n < 1_000_000) {
    const v = n / 1000;
    return `${trim(v)}K`;
  }
  const v = n / 1_000_000;
  return `${trim(v)}M`;
}

function trim(v: number): string {
  // One decimal place, but drop a trailing ".0".
  const s = v.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

/** Stable 0-359 hue derived from a seed string (used for per-stream gradients). */
export function hueFromSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

/** A deep, on-brand diagonal gradient seeded by `seed` — a thumbnail stand-in. */
export function gradientFor(seed: string): string {
  const h = hueFromSeed(seed);
  const h2 = (h + 48) % 360;
  return `linear-gradient(135deg, hsl(${h} 70% 16%), hsl(${h2} 65% 9%))`;
}

/** Compact relative time: "just now", "5m", "3h", "2d", else a short date. */
export function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Elapsed time since startISO as "H:MM:SS" (e.g. "1:23:40"), or "" when null/invalid. */
export function formatUptime(startISO: string | null): string {
  if (!startISO) return "";
  const start = new Date(startISO).getTime();
  if (Number.isNaN(start)) return "";
  const diff = Math.max(0, Date.now() - start);
  const total = Math.floor(diff / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
